import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './scss/EditTeam.scss';
import Header from './Header';
import api from './services/apiService';
import { FaArrowLeft, FaSave, FaUserPlus, FaTrash } from 'react-icons/fa';

const EditTeam = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Check if user is authorized (coach or manager)
  const userType = sessionStorage.getItem('user_type');
  const userName = sessionStorage.getItem('first_name') + ' ' + sessionStorage.getItem('last_name');
  const isAuthorized = userType === 'coach' || userType === 'manager';
  
  // Form state
  const [formData, setFormData] = useState({
    team_name: '',
    league_name: '',
    league_table_url: '',
    fixtures_url: '',
    location: '',
    pitch: '',
    play_schedule: '',
    open_to_new_players: true,
    type: '',
    age_group: '',
    // Team management fields
    admin: { name: '', contact: '' },
    emergency_contact: '',
    complaints_procedure: '',
    safeguarding: '',
    safeguarding_url: ''
  });
  
  // Coaches/managers state
  const [coaches, setCoaches] = useState([]);
  const [newCoach, setNewCoach] = useState({ name: '', role: 'Coach', contact: '' });
  
  useEffect(() => {
    // Redirect if not authorized
    if (!isAuthorized) {
      navigate(`/team/${teamId}`);
      return;
    }
    
    // Fetch team details
    const fetchTeamDetails = async () => {
      setIsLoading(true);
      try {
        const response = await api.get(`fetch_team_details.php?team_id=${teamId}`);
        if (response.data.success) {
          const team = response.data.team;
          
          setFormData({
            team_name: team.team_name || '',
            league_name: team.league_name || '',
            league_table_url: team.league_table_url || '',
            fixtures_url: team.fixtures_url || '',
            location: team.location || '',
            pitch: team.pitch || '',
            play_schedule: team.play_schedule || '',
            open_to_new_players: team.open_to_new_players === 1,
            type: team.type || '',
            age_group: team.age_group || '',
            // Team management fields
            admin: team.admin || { name: '', contact: '' },
            emergency_contact: team.emergency_contact || '',
            complaints_procedure: team.complaints_procedure || '',
            safeguarding: team.safeguarding || '',
            safeguarding_url: team.safeguarding_url || ''
          });
          
          // Set coaches if available
          if (team.coaches && Array.isArray(team.coaches)) {
            setCoaches(team.coaches);
          }
        } else {
          setError('Failed to fetch team details');
        }
      } catch (err) {
        console.error('Error fetching team details:', err);
        setError('An error occurred while fetching team details');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTeamDetails();
  }, [teamId, isAuthorized, navigate]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleAdminChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      admin: {
        ...prev.admin,
        [name]: value
      }
    }));
  };
  
  const handleNewCoachChange = (e) => {
    const { name, value } = e.target;
    setNewCoach(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleAddCoach = () => {
    // Validate required fields
    if (!newCoach.name.trim()) {
      return;
    }
    
    // Add coach to list
    setCoaches(prev => [...prev, { ...newCoach }]);
    // Reset form
    setNewCoach({ name: '', role: 'Coach', contact: '' });
  };
  
  const handleRemoveCoach = (index) => {
    // Get the coach information
    const coach = coaches[index];
    
    // Check if this coach is the logged in user
    if (coach.name === userName) {
      setError("You cannot remove yourself as a coach.");
      return;
    }
    
    setCoaches(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccessMessage('');
    
    try {
      // Format data for API
      const apiData = {
        ...formData,
        team_id: teamId,
        open_to_new_players: formData.open_to_new_players ? 1 : 0,
        coaches: coaches
      };
      
      const response = await api.post('update_team_information.php', apiData);
      
      if (response.data.success) {
        setSuccessMessage('Team information updated successfully');
        // Navigate back to team page after a short delay
        setTimeout(() => {
          navigate(`/team/${teamId}`);
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to update team information');
      }
    } catch (err) {
      console.error('Error updating team:', err);
      setError('An error occurred while updating team information');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleCancel = () => {
    navigate(`/team/${teamId}`);
  };
  
  if (isLoading) {
    return (
      <div className="edit-team-page">
        <Header />
        <div className="container">
          <div className="loading">Loading team information...</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="edit-team-page">
      <Header />
      <div className="container">
        <div className="page-header">
          <button className="back-button" onClick={handleCancel}>
            <FaArrowLeft /> Back to Team
          </button>
          <h1 className="page-title">Edit Team Information</h1>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>Basic Information</h2>
            
            <div className="form-group">
              <label htmlFor="team_name">Team Name*</label>
              <input
                type="text"
                id="team_name"
                name="team_name"
                value={formData.team_name}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="type">Team Type</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
              >
                <option value="friendly">Friendly</option>
                <option value="squad">Squad</option>
                <option value="academy">Academy</option>
              </select>
            </div>
            
            {formData.type === 'academy' && (
              <div className="form-group">
                <label htmlFor="age_group">Age Group</label>
                <select
                  id="age_group"
                  name="age_group"
                  value={formData.age_group}
                  onChange={handleChange}
                >
                  <option value="">Select Age Group</option>
                  <option value="U7s">U7s</option>
                  <option value="U8s">U8s</option>
                  <option value="U9s">U9s</option>
                  <option value="U10s">U10s</option>
                  <option value="U11s">U11s</option>
                  <option value="U12s">U12s</option>
                  <option value="U13s">U13s</option>
                  <option value="U14s">U14s</option>
                  <option value="U15s">U15s</option>
                  <option value="U16s">U16s</option>
                  <option value="U17s">U17s</option>
                  <option value="U18s">U18s</option>
                  <option value="U21s">U21s</option>
                  <option value="U23s">U23s</option>
                </select>
              </div>
            )}
          </div>
          
          <div className="form-section">
            <h2>League Information</h2>
            
            <div className="form-group">
              <label htmlFor="league_name">League Name</label>
              <input
                type="text"
                id="league_name"
                name="league_name"
                value={formData.league_name}
                onChange={handleChange}
                placeholder="e.g. North London Junior League"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="league_table_url">
                FA League Table URL
                <span className="format-hint">Get this from the FA Full-Time website</span>
              </label>
              <input
                type="url"
                id="league_table_url"
                name="league_table_url"
                value={formData.league_table_url}
                onChange={handleChange}
                placeholder="https://fulltime.thefa.com/..."
              />
              <div className="field-description">The FA Full-Time website URL for your team's league table</div>
            </div>
            
            <div className="form-group">
              <label htmlFor="fixtures_url">
                FA Fixtures URL
                <span className="format-hint">Get this from the FA Full-Time website fixtures page</span>
              </label>
              <input
                type="url"
                id="fixtures_url"
                name="fixtures_url"
                value={formData.fixtures_url}
                onChange={handleChange}
                placeholder="https://fulltime.thefa.com/..."
              />
              <div className="field-description">The FA Full-Time website URL for your team's fixtures</div>
            </div>
          </div>
          
          <div className="form-section">
            <h2>Location & Schedule</h2>
            
            <div className="form-group">
              <label htmlFor="location">Home Ground*</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                placeholder="e.g. Highbury Fields"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="pitch">Pitch Details</label>
              <input
                type="text"
                id="pitch"
                name="pitch"
                value={formData.pitch}
                onChange={handleChange}
                placeholder="e.g. Astroturf - Field 3"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="play_schedule">Play Schedule</label>
              <textarea
                id="play_schedule"
                name="play_schedule"
                value={formData.play_schedule}
                onChange={handleChange}
                placeholder="e.g. Saturday mornings, 10am-12pm. Training on Wednesdays 6-7pm."
                rows={3}
              />
            </div>
          </div>
          
          {/* Show Team Management for all team types */}
          <div className="form-section">
              <h2>Team Management</h2>
              
              {/* Coaches/Managers section */}
              <div className="coaches-section">
                <h3>Coaches/Managers</h3>
                
                {coaches.length > 0 && (
                  <div className="coaches-list">
                    {coaches.map((coach, index) => (
                      <div key={index} className="coach-item">
                        <div className="coach-details">
                          <div className="coach-name">{coach.name}</div>
                          <div className="coach-role">{coach.role}</div>
                          {coach.contact && <div className="coach-contact">{coach.contact}</div>}
                        </div>
                        <button 
                          type="button" 
                          className="remove-coach" 
                          onClick={() => handleRemoveCoach(index)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="add-coach-form">
                  <div className="form-group">
                    <label htmlFor="coach-name">Name*</label>
                    <input
                      type="text"
                      id="coach-name"
                      name="name"
                      value={newCoach.name}
                      onChange={handleNewCoachChange}
                      placeholder="e.g. John Smith"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="coach-role">Role</label>
                    <select
                      id="coach-role"
                      name="role"
                      value={newCoach.role}
                      onChange={handleNewCoachChange}
                    >
                      <option value="Coach">Coach</option>
                      <option value="Assistant Coach">Assistant Coach</option>
                      <option value="Manager">Manager</option>
                      <option value="Team Captain">Team Captain</option>
                      <option value="Squad Leader">Squad Leader</option>
                      <option value="Team Admin">Team Admin</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="coach-contact">Contact (Email/Phone)</label>
                    <input
                      type="text"
                      id="coach-contact"
                      name="contact"
                      value={newCoach.contact}
                      onChange={handleNewCoachChange}
                      placeholder="e.g. john@example.com or 07123456789"
                    />
                  </div>
                  
                  <button type="button" className="add-button" onClick={handleAddCoach}>
                    <FaUserPlus /> Add Team Contact
                  </button>
                </div>
              </div>
              
              {/* Team Admin section */}
              <div className="admin-section">
                <h3>Team Contact</h3>
                <p className="section-description">This is the person responsible for team inquiries, payments, and general information</p>
                
                <div className="form-group">
                  <label htmlFor="admin-name">Contact Name</label>
                  <input
                    type="text"
                    id="admin-name"
                    name="name"
                    value={formData.admin.name}
                    onChange={handleAdminChange}
                    placeholder="e.g. Jane Smith"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="admin-contact">Contact Details</label>
                  <input
                    type="text"
                    id="admin-contact"
                    name="contact"
                    value={formData.admin.contact}
                    onChange={handleAdminChange}
                    placeholder="e.g. jane@example.com or 07123456789"
                  />
                </div>
              </div>
              
              {/* Emergency Contact */}
              <div className="form-group">
                <label htmlFor="emergency_contact">
                  Emergency Contact Details
                  <span className="format-hint">Add line breaks for name, email, and phone on separate lines</span>
                </label>
                <textarea
                  id="emergency_contact"
                  name="emergency_contact"
                  value={formData.emergency_contact}
                  onChange={handleChange}
                  placeholder="Example format:
John Smith, Welfare Officer
john.smith@example.com
07700 900000"
                  rows={5}
                  className="textarea-field"
                />
                <div className="field-description">This information will be displayed with the first line as a title and remaining lines as contact details.</div>
              </div>
              
              {/* Complaints Procedure */}
              <div className="form-group">
                <label htmlFor="complaints_procedure">Complaints Procedure</label>
                <textarea
                  id="complaints_procedure"
                  name="complaints_procedure"
                  value={formData.complaints_procedure}
                  onChange={handleChange}
                  placeholder="Describe how complaints should be addressed within the club"
                  rows={4}
                  className="textarea-field"
                />
              </div>
              
              {/* Safeguarding - only show for academy teams */}
              {formData.type === 'academy' && (
                <>
                  <div className="form-group">
                    <label htmlFor="safeguarding">Safeguarding Information</label>
                    <textarea
                      id="safeguarding"
                      name="safeguarding"
                      value={formData.safeguarding}
                      onChange={handleChange}
                      placeholder="Provide safeguarding officer information and contact procedure"
                      rows={4}
                      className="textarea-field"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="safeguarding_url">Safeguarding Policy URL</label>
                    <input
                      type="url"
                      id="safeguarding_url"
                      name="safeguarding_url"
                      value={formData.safeguarding_url}
                      onChange={handleChange}
                      placeholder="https://..."
                    />
                  </div>
                </>
              )}
            </div>
          
          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={handleCancel}>
              Cancel
            </button>
            <button type="submit" className="save-button" disabled={isSaving}>
              <FaSave /> {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTeam; 